import { useCallback, useEffect, useId, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import NavBar from './nav_bar.js';
import BackButton from './BackButton.js';
import usePageSeo from './seo.js';
import SupportLine from './support_line.js';
import './index.css';

const PAGE_DESC =
  'New to MP3JUUG? Claim your first pack of 5 JUUGs (blank NFC music cards) free — '
  + 'just give a US shipping address. One pack per artist.';

// /free-juugs — first-time artists claim one pack of blank JUUGs free by
// giving a shipping address instead of paying. One claim per email; the
// claim lands as 'pending' and a person ships it. Backend: /free-juugs/*
// in server.js, logic in src/backend/freeJuugs.js.
function FreeJuugs() {
  const [params] = useSearchParams();

  const [count, setCount] = useState(5);
  const [configured, setConfigured] = useState(true);
  const [claimed, setClaimed] = useState(false);
  const [alreadyOrdered, setAlreadyOrdered] = useState(false);
  // true until we've checked a real email and found no uploads for it
  const [hasUploaded, setHasUploaded] = useState(true);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState('');
  const [email, setEmail] = useState((params.get('email') || '').trim());
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [postalCode, setPostalCode] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const nameId = useId();
  const emailId = useId();
  const line1Id = useId();
  const line2Id = useId();
  const cityId = useId();
  const stateId = useId();
  const zipId = useId();

  usePageSeo({ title: 'Claim 5 free JUUGs | MP3JUUG', description: PAGE_DESC });

  const checkStatus = useCallback(async (forEmail) => {
    try {
      const qs = forEmail ? `?email=${encodeURIComponent(forEmail)}` : '';
      const res = await fetch(`/free-juugs/status${qs}`);
      const data = await res.json();
      setConfigured(data.configured !== false);
      if (Number.isFinite(data.count)) setCount(data.count);
      setClaimed(!!data.claimed);
      setAlreadyOrdered(!!data.alreadyOrdered);
      // only gate on this once we've actually checked an email
      setHasUploaded(forEmail ? !!data.hasUploaded : true);
    } catch {
      /* leave defaults — the claim POST is the real gate */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkStatus(email.trim()); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch('/free-juugs/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim(),
          address: {
            line1: line1.trim(),
            line2: line2.trim(),
            city: city.trim(),
            state: stateCode.trim(),
            postalCode: postalCode.trim(),
          },
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.msg || 'Could not record your claim.');
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }, [email, name, line1, line2, city, stateCode, postalCode]);

  const shell = (children) => (
    <>
      <NavBar />
      <main className='page'>
        <div className='page-content'>
          <BackButton fallback='/upload' />
          {children}
          <SupportLine />
        </div>
      </main>
    </>
  );

  if (loading) return shell(<p className='help-text'>Loading…</p>);

  if (!configured) {
    return shell(
      <div className='card'>
        <h1>Free JUUGs</h1>
        <p className='lead'>The free JUUG offer isn’t open right now.</p>
        <Link to='/media' className='btn btn-primary'>See the JUUG store</Link>
      </div>,
    );
  }

  if (done || claimed) {
    return shell(
      <div className='card'>
        <h1>{done ? 'Claim received' : 'You’re all set'}</h1>
        <p className='lead'>
          Your {count} free JUUGs are on the way. We’ll email {email || 'you'} when they ship.
        </p>
        <p>
          Once they arrive, head to <Link to='/link'>links</Link> to generate a one-time
          link and write one onto each card.
        </p>
        <Link to='/upload' className='btn btn-secondary'>Upload music</Link>
      </div>,
    );
  }

  if (alreadyOrdered) {
    return shell(
      <div className='card'>
        <h1>Claim 5 free JUUGs</h1>
        <p className='lead'>
          Looks like you’ve ordered JUUGs before — the free pack is for first-timers.
        </p>
        <Link to='/media' className='btn btn-primary'>Order more JUUGs</Link>
        <p className='help-text'>
          Wrong email?{' '}
          <button
            type='button'
            className='btn btn-secondary'
            onClick={() => { setAlreadyOrdered(false); }}
          >
            Use a different one
          </button>
        </p>
      </div>,
    );
  }

  if (email.trim() && !hasUploaded) {
    return shell(
      <div className='card'>
        <h1>Claim 5 free JUUGs</h1>
        <p className='lead'>
          The free pack is for new artists — upload your first release, then come back
          here to claim {count} JUUGs to write your links onto.
        </p>
        <Link to='/upload' className='btn btn-primary'>Upload your music</Link>
        <p className='help-text'>
          Uploaded with a different email?{' '}
          <button
            type='button'
            className='btn btn-secondary'
            onClick={() => setHasUploaded(true)}
          >
            Use that email
          </button>
        </p>
      </div>,
    );
  }

  return shell(
    <>
      <div className='card'>
        <h1>Claim 5 free JUUGs</h1>
        <p className='lead'>
          New here? Your first pack of {count} JUUGs (blank NFC cards) is on us — just tell us
          where to ship them. One pack per artist. We apply your cover art before it ships.
        </p>
      </div>

      <form className='card' onSubmit={submit}>
        <div className='field'>
          <label className='label' htmlFor={emailId}>Email</label>
          <input
            id={emailId}
            className='input'
            type='email'
            autoComplete='email'
            inputMode='email'
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => checkStatus(email.trim())}
          />
          <p className='help-text'>Use the email you upload with.</p>
        </div>

        <div className='field'>
          <label className='label' htmlFor={nameId}>Name for the label</label>
          <input
            id={nameId}
            className='input'
            type='text'
            autoComplete='name'
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className='field'>
          <label className='label' htmlFor={line1Id}>Street address</label>
          <input
            id={line1Id}
            className='input'
            type='text'
            autoComplete='address-line1'
            required
            value={line1}
            onChange={(e) => setLine1(e.target.value)}
          />
        </div>

        <div className='field'>
          <label className='label' htmlFor={line2Id}>Apt / unit (optional)</label>
          <input
            id={line2Id}
            className='input'
            type='text'
            autoComplete='address-line2'
            value={line2}
            onChange={(e) => setLine2(e.target.value)}
          />
        </div>

        <div className='field'>
          <label className='label' htmlFor={cityId}>City</label>
          <input
            id={cityId}
            className='input'
            type='text'
            autoComplete='address-level2'
            required
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
        </div>

        <div className='field'>
          <label className='label' htmlFor={stateId}>State</label>
          <input
            id={stateId}
            className='input'
            type='text'
            autoComplete='address-level1'
            required
            maxLength={2}
            placeholder='GA'
            value={stateCode}
            onChange={(e) => setStateCode(e.target.value.toUpperCase())}
          />
        </div>

        <div className='field'>
          <label className='label' htmlFor={zipId}>ZIP code</label>
          <input
            id={zipId}
            className='input'
            type='text'
            autoComplete='postal-code'
            inputMode='numeric'
            required
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
          />
        </div>

        <p className='help-text'>Ships within the US only.</p>
        {error && <p role='alert' className='error-text'>{error}</p>}
        <button type='submit' className='btn btn-primary' disabled={submitting}>
          {submitting ? 'Sending…' : `Send my ${count} free JUUGs`}
        </button>
      </form>
    </>,
  );
}

export default FreeJuugs;
